import type { AfterViewInit, OnInit, QueryList } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject, ViewChildren } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import type { Subscription } from 'rxjs';

import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import type { AllocatePoolValidatedMegaResponse } from '../services/interfaces/allocate-pool-validated-mega-response';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';
import { UserService } from '../../shared/services/user.service';

@Component({
  standalone: false,
  selector: 'invd-allocate-pools',
  templateUrl: './allocate-pools.component.html',
  styleUrls: ['./allocate-pools.component.scss'],
})
export class AllocatePoolsComponent implements OnInit, AfterViewInit {
  countForTotalMegas = 0;
  countForAllocatedMegas = 0;
  countForRejectMegas = 0;
  isConfirmButtonDisabled = true;
  isAllocateAllDisabled = false;
  isRejectAllDisabled = false;
  displayResetLink = true;
  validatedMegaList: any;
  updatedFeeList: any;
  isAllowToNavigateAway: boolean;
  displayNavigateAwayPopup = false;
  showFreddieIssuedAmtWarning = false;
  freddieWarningMessage = '';
  commitRequest: any;
  destinationUrl: any;
  navSubscription: Subscription;
  @ViewChildren('allocateRadio') allocateRadio: QueryList<any>;
  @ViewChildren('allocateLabel') allocateLabel: QueryList<any>;
  @ViewChildren('rejectRadio') rejectRadio: QueryList<any>;
  @ViewChildren('rejectLabel') rejectLabel: QueryList<any>;
  public totalCountFromValidatePools = 0;
  public totalMegaErrorsCount = 0;
  public megaType: string[] = [];
  public disclaimerMessage = '';
  private cuMultiMegaCommitRequestPayload = [];
  private feeTickValue = 0;

  public fmLoadingPageService = inject(FmLoadingPageService);
  private megaService = inject(MegasService);
  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private userService = inject(UserService);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.navigationController();
    this.fmLoadingPageService.isLoaderRequesting.set(true);

    this.getValidatedMegas();
    this.totalCountFromValidatePools = this.passDataService.getTotalCountFromValidatePools();

    this.getUpdatedFee();
  }

  ngAfterViewInit() {
    this.onInitTooltipError();
  }

  private onInitTooltipError(): void {}

  public getUpdatedFee(): void {
    const detectedMegaList: any = this.passDataService.getDetectedMegaList();
    const request = [];

    detectedMegaList.forEach((mega) => {
      if (mega.validationStatus === 'Complete' && mega.freddiUPBAmount > 0) {
        const megaObject: any = new Object();
        megaObject.poolNumber = mega.megaNumber;
        megaObject.freddieIssuedAmt = mega.freddiUPBAmount;
        if (megaObject.freddieIssuedAmt > 0) {
          megaObject.hasFreddieCollateral = true;
        } else {
          megaObject.hasFreddieCollateral = false;
        }
        request.push(megaObject);
      }
    });

    if (request.length > 0) {
      this.megaService.collateralUploadCalculateFee(request).subscribe({
        next: (data) => this.handleUpdatedFeeSuccess(data),
        error: (error) => this.handleUpdatedFeeFail(error),
      });
    }
  }

  public handleUpdatedFeeSuccess(data: any): void {
    this.updatedFeeList = data;
    data.forEach((mega) => {
      if (mega.hasFreddieCollateral) {
        this.feeTickValue = mega.freFeeTicksIndicator;
        this.passDataService.setFeeTickValue(mega.freFeeTicksIndicator);
      }
    });
    this.updateFees();
    this.cdr.markForCheck();
  }

  public handleUpdatedFeeFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public updateFees(): void {
    if (!this.validatedMegaList) {
      return;
    }

    this.validatedMegaList.forEach((mega) => {
      const updatedFee = this.updatedFeeList.find((fee) => fee.poolNumber === mega.megaNumber);
      if (updatedFee) {
        mega.feeDollars = updatedFee.feeDollars;
        mega.freddieIssuedAmt = updatedFee.freddieIssuedAmt;
      }
    });
  }

  public getValidatedMegas(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    this.megaService.validatedMegaList(request).subscribe({
      next: (data) =>
        this.handleValidatedMegaListSuccess(data as AllocatePoolValidatedMegaResponse),
      error: (error) => this.handleAWSValidatedMegaListServiceFail(error),
    });
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public navigationController() {
    this.navSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('allocate-pools') > 0) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('confirmation') > 0) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('validate-pools') > 0) {
          this.isAllowToNavigateAway = false;
          this.displayNavigateAwayPopup = true;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('upload-pool-info') > 0) {
          this.isAllowToNavigateAway = false;
          this.displayNavigateAwayPopup = true;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('error') > 0) {
          this.isAllowToNavigateAway = true;
        } else {
          this.displayNavigateAwayPopup = true;
          this.isAllowToNavigateAway = false;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public handleValidatedMegaListSuccess(validatedMegaList: AllocatePoolValidatedMegaResponse) {
    this.validatedMegaList = validatedMegaList && validatedMegaList.validatedMegas;
    this.countForTotalMegas = this.validatedMegaList.length;
    this.totalMegaErrorsCount = this.totalCountFromValidatePools - this.countForTotalMegas;
    this.megaType = [];
    this.validatedMegaList.forEach((item) => {
      if (item && (item.megaType === 'SFFIXMEGA' || item.megaType === 'MFFIXMEGA')) {
        this.megaType.push('Pass-Through Rate');
      } else {
        this.megaType.push('Initial Accrual Rate');
      }
    });
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handleAWSValidatedMegaListServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public navigateToConfirmationPage(): void {
    this.isConfirmButtonDisabled = true;
    this.fmLoadingPageService.isLoaderRequesting.set(true);

    const request: any = [];
    const freddieDeals: any = [];

    if (this.updatedFeeList && this.updatedFeeList.length > 0) {
      this.validatedMegaList.forEach((item) => {
        const updatedFee = this.updatedFeeList.find((fee) => fee.poolNumber === item.megaNumber);
        if (updatedFee && updatedFee.hasFreddieCollateral && item.status === 'allocate') {
          const commitObject: any = new Object();
          commitObject.poolNumber = updatedFee.poolNumber;
          commitObject.freddieIssuedAmt = updatedFee.freddieIssuedAmt;
          commitObject.hasFreddieCollateral = updatedFee.hasFreddieCollateral;
          commitObject.packageRevisionNumber = updatedFee.packageRevisionNumber;
          commitObject.tradeRevisionNumber = updatedFee.tradeRevisionNumber;

          if (updatedFee.freddieIssuedAmt / item.issueAmount > 0.5) {
            freddieDeals.push(updatedFee.poolNumber);
          }

          request.push(commitObject);
        }
      });
    }

    if (freddieDeals.length > 0) {
      this.commitRequest = request;
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.showFreddieIssuedAmtWarning = true;
      if (freddieDeals.length === 1) {
        this.freddieWarningMessage = `<b>${freddieDeals[0]}</b> contains over 50% Freddie Mac collateral. Please note that continuing
        may result in a higher commingling fee than you anticipated.`;
      } else {
        this.freddieWarningMessage = `<b>${freddieDeals.slice(0, -1).join(', ')}</b> and <b>${freddieDeals.slice(-1)}</b> contain over 50% Freddie Mac collateral. Please note that continuing
        may result in a higher commingling fee than you anticipated.`;
      }
    } else {
      this.continueToCommit(request);
    }
  }

  public continueToCommit(request: any): void {
    this.showFreddieIssuedAmtWarning = false;
    if (request.length > 0) {
      this.cuMultiMegaCommitRequestPayload = request;

      this.megaService.cuMultiMegaCommit(request).subscribe({
        next: (data) => this.handleCUCommitSuccess(data),
        error: (error) => this.handleCUCommitFail(error),
      });
    } else {
      const commitReq: any = new Object();

      const req: Array<any> = new Array<any>();

      for (const validatedMega of this.validatedMegaList) {
        const commitObject = {
          megaNumber: validatedMega.megaNumber,
          allocate: validatedMega.status === 'allocate' ? 'Y' : 'N',
          rejectReason: null,
          hasFreddieCollateral: false,
        };
        this.cuMultiMegaCommitRequestPayload.forEach((item) => {
          if (item.poolNumber === validatedMega.megaNumber) {
            commitObject.hasFreddieCollateral = item.hasFreddieCollateral;
          }
        });
        req.push(commitObject);
      }

      commitReq.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
      commitReq.megaDetails = req;

      this.passDataService.setMultiMegaCommitRequest(commitReq);

      this.megaService.uploadCollateralCommit(commitReq).subscribe({
        next: (data) => this.handeluploadCollateralCommitSuccess(data),
        error: (error) => this.handleuploadCollateralCommitFail(error),
      });
    }
  }

  public cancelCommit(): void {
    this.showFreddieIssuedAmtWarning = false;
    this.isConfirmButtonDisabled = false;
  }

  public allocationStatus(idx): void {
    this.countForAllocatedMegas = 0;
    if (this.validatedMegaList[idx].status === 'reject') {
      this.countForRejectMegas--;
    }
    if (this.allocateRadio.toArray()[idx].nativeElement.name === 'allocate') {
      this.allocateLabel.toArray()[idx].nativeElement.classList.add('allocate-div-click');
      this.rejectLabel.toArray()[idx].nativeElement.classList.remove('reject-div-click');
      this.validatedMegaList[idx].status = 'allocate';
    }

    this.validatedMegaList.forEach((item) => {
      if (item.status === 'allocate') {
        this.countForAllocatedMegas++;
        this.isAllocateAllDisabled = true;
      }
    });
    this.megaCountStatusPositive();
  }

  public rejectionStatus(idx): void {
    this.countForRejectMegas = 0;
    if (this.validatedMegaList[idx].status === 'allocate') {
      this.countForAllocatedMegas--;
    }
    if (this.rejectRadio.toArray()[idx].nativeElement.name === 'reject') {
      this.rejectLabel.toArray()[idx].nativeElement.classList.add('reject-div-click');
      this.allocateLabel.toArray()[idx].nativeElement.classList.remove('allocate-div-click');
      this.validatedMegaList[idx].status = 'reject';
    }

    this.validatedMegaList.forEach((item) => {
      if (item.status === 'reject') {
        this.countForRejectMegas++;
      }
    });
    this.megaCountStatusPositive();
  }

  public megaCountStatusPositive(): void {
    this.countForAllocatedMegas = this.countForAllocatedMegas < 0 ? 0 : this.countForAllocatedMegas;
    this.countForRejectMegas = this.countForRejectMegas < 0 ? 0 : this.countForRejectMegas;
    this.isAllocateAllDisabled =
      this.countForAllocatedMegas === this.countForTotalMegas ? true : false;
    this.isRejectAllDisabled = this.countForRejectMegas === this.countForTotalMegas ? true : false;
    this.isConfirmButtonDisabled =
      this.countForTotalMegas === this.countForAllocatedMegas + this.countForRejectMegas
        ? false
        : true;
  }

  public handleAllocateAll(): void {
    this.countForAllocatedMegas = 0;
    for (let i = 0; i < this.allocateRadio.length; i++) {
      if (this.allocateRadio.toArray()[i].nativeElement.name === 'allocate') {
        this.allocateLabel.toArray()[i].nativeElement.classList.add('allocate-div-click');
        this.rejectLabel.toArray()[i].nativeElement.classList.remove('reject-div-click');
        this.validatedMegaList[i].status = 'allocate';
      }
    }
    this.validatedMegaList.forEach((item) => {
      if (item.status === 'allocate') {
        this.countForAllocatedMegas++;
      }
    });
    this.countForRejectMegas = 0;
    this.isAllocateAllDisabled = true;
    this.displayResetLink = false;
    this.megaCountStatusPositive();
    this.isConfirmButtonDisabled = false;
  }

  public handleRejectAll(): void {
    this.countForRejectMegas = 0;
    for (let i = 0; i < this.rejectRadio.length; i++) {
      if (this.rejectRadio.toArray()[i].nativeElement.name === 'reject') {
        this.rejectLabel.toArray()[i].nativeElement.classList.add('reject-div-click');
        this.allocateLabel.toArray()[i].nativeElement.classList.remove('allocate-div-click');
        this.validatedMegaList[i].status = 'reject';
      }
    }
    this.validatedMegaList.forEach((item) => {
      if (item.status === 'reject') {
        this.countForRejectMegas++;
      }
    });
    this.countForAllocatedMegas = 0;
    this.isRejectAllDisabled = true;
    this.displayResetLink = false;
    this.megaCountStatusPositive();
    this.isConfirmButtonDisabled = false;
  }

  public handleResetSelection(): void {
    for (let i = 0; i < this.validatedMegaList.length; i++) {
      if (
        this.allocateRadio.toArray()[i].nativeElement.name === 'allocate' ||
        this.rejectRadio.toArray()[i].nativeElement.name === 'reject'
      ) {
        this.allocateLabel.toArray()[i].nativeElement.classList.remove('allocate-div-click');
        this.rejectLabel.toArray()[i].nativeElement.classList.remove('reject-div-click');
        this.validatedMegaList[i].status = '';
      }
    }
    this.countForAllocatedMegas = 0;
    this.countForRejectMegas = 0;
    this.displayResetLink = true;
    this.isRejectAllDisabled = false;
    this.isAllocateAllDisabled = false;
    this.isConfirmButtonDisabled = true;
  }

  public navigatePageTo(pageURL) {
    this.router.navigate([pageURL]);
    this.isAllowToNavigateAway = true;
    this.passDataService.setIsUploadCancelledFromValidatePoolsPage(false);
    if (!(pageURL.indexOf('validate-pools') > 0)) {
      this.cancelUpload();
    }
  }

  public cancelUpload(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    this.megaService.cancelUpload(request).subscribe({
      next: (data) => this.handelCancelUploadSuccess(data),
      error: (error) => this.handleCancelUploadFail(error),
    });
  }

  public handelCancelUploadSuccess(_response: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.isAllowToNavigateAway = true;
    this.cdr.markForCheck();
  }

  public handleCancelUploadFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handeluploadCollateralCommitSuccess(response: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.passDataService.setCommitUploadCollateralResponse(response);

    this.isAllowToNavigateAway = true;
    this.cdr.markForCheck();
    this.router.navigate(['/mega-upload/upload-pool-info/confirmation']);
  }

  public handleuploadCollateralCommitFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handleCUCommitSuccess(_response: any): void {
    const commitReq: any = new Object();

    const req: Array<any> = new Array<any>();

    for (const validatedMega of this.validatedMegaList) {
      const commitObject = {
        megaNumber: validatedMega.megaNumber,
        allocate: validatedMega.status === 'allocate' ? 'Y' : 'N',
        rejectReason: null,
        hasFreddieCollateral: false,
      };
      this.cuMultiMegaCommitRequestPayload.forEach((item) => {
        if (item.poolNumber === validatedMega.megaNumber) {
          commitObject.hasFreddieCollateral = item.hasFreddieCollateral;
        }
      });
      req.push(commitObject);
    }

    commitReq.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    commitReq.megaDetails = req;

    this.passDataService.setMultiMegaCommitRequest(commitReq);

    this.megaService.uploadCollateralCommit(commitReq).subscribe({
      next: (data) => this.handeluploadCollateralCommitSuccess(data),
      error: (error) => this.handleuploadCollateralCommitFail(error),
    });
  }

  public handleCUCommitFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public disclaimerCheck(mega: any): boolean {
    if (!this.userService.isFedUser && mega.freddieIssuedAmt > 0) {
      return true;
    }
    return false;
  }

  public needsDisclaimer(): void {
    this.disclaimerMessage = StringConstants.freddieComputedFeeDisclaimer(this.feeTickValue);
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
